<template>
  <div
    class="allocation-bar"
    :class="[`allocation-bar--${summary.status}`, { 'allocation-bar--selected': selected, 'allocation-bar--compact': compact }]"
    role="group"
    :aria-label="barAriaLabel"
    :data-selected="selected ?? false"
    tabindex="0"
    @click="emit('selectAllocation')"
    @keydown.enter.prevent="emit('selectAllocation')"
    @keydown.space.prevent="emit('selectAllocation')"
  >
    <!-- Side placement so the tooltip never covers the allocation row above the chip. -->
    <v-tooltip v-for="(segment, segmentIndex) in summary.segments" :key="segment.key" location="end" open-delay="150">
      <template #activator="{ props: activatorProps }">
        <component
          :is="isPlainPoSegment(segment) ? 'button' : 'div'"
          v-bind="activatorProps"
          class="allocation-bar__segment"
          :class="[
            segment.type === SEGMENT_TYPE.remains ? 'allocation-bar__segment--remains' : segment.type === SEGMENT_TYPE.manual ? 'allocation-bar__segment--manual' : 'allocation-bar__segment--po',
            segmentHighlightClass(segment),
            segmentHoverRingClass(segment),
            segmentSelectionRingClass(segment),
            segmentRelatedClass(segment),
            segmentMutedClass(segment),
          ]"
          :style="{ width: `${segment.percent}%`, backgroundColor: segmentColor(segment, segmentIndex) }"
          :type="isPlainPoSegment(segment) ? 'button' : undefined"
          :tabindex="isPlainPoSegment(segment) ? undefined : 0"
          :draggable="isDragEnabled && isPlainPoSegment(segment)"
          :aria-label="segmentAriaLabel(segment)"
          @click="onSegmentClick($event, segment)"
          @mouseenter="onSegmentHoverStart(segment)"
          @mouseleave="onSegmentHoverEnd()"
          @focus="onSegmentHoverStart(segment)"
          @blur="onSegmentHoverEnd()"
          @dragstart="onSegmentDragStart($event, segment)"
          @dragend="onSegmentDragEnd()"
        >
          <span v-if="segment.percent >= MIN_LABEL_PERCENT" class="allocation-bar__label" :style="segmentLabelStyle(segment, segmentIndex)">
            {{ segmentLabel(segment) }}
          </span>
          <AllocationGroupHandle
            v-if="isDragEnabled && groupHandleInfo(segment)"
            :distribution-group-id="segment.distributionGroupId!"
            :vendor-id="vendorId"
            :week-key="weekKey"
            :member-capacity-type-ids="groupHandleInfo(segment)!.memberCapacityTypeIds"
          />
        </component>
      </template>
      <div class="allocation-bar__tooltip">
        <p class="allocation-bar__tooltip-line">{{ segment.label }}</p>
        <p v-if="!privacyStore.hideCustomerMappings && segmentCustomerName(segment)" class="allocation-bar__tooltip-line">Customer: {{ segmentCustomerName(segment) }}</p>
        <p class="allocation-bar__tooltip-line">Quantity: {{ formatQuantity(segment.quantity) }}</p>
        <p class="allocation-bar__tooltip-line">Share: {{ formatPercent(segment.percent) }}</p>
        <div v-if="groupBreakdowns.get(segment.key)" class="allocation-bar__tooltip-group">
          <p class="allocation-bar__tooltip-line allocation-bar__tooltip-group-heading">
            Distributed across {{ groupBreakdowns.get(segment.key)!.members.length }} types
          </p>
          <p
            v-for="member in groupBreakdowns.get(segment.key)!.members"
            :key="member.typeCode"
            class="allocation-bar__tooltip-line"
            :class="{ 'allocation-bar__tooltip-member--current': member.typeCode === typeCode }"
          >
            {{ member.typeCode === typeCode ? '▸ ' : '' }}{{ member.typeCode }}: {{ formatQuantity(member.quantity)
            }}{{ member.typeCode === typeCode ? ' (this row)' : '' }}
          </p>
          <p class="allocation-bar__tooltip-line">Total: {{ formatQuantity(groupBreakdowns.get(segment.key)!.total) }}</p>
        </div>
      </div>
    </v-tooltip>
    <v-tooltip v-if="summary.nonCapacityPos.length" location="end" open-delay="150">
      <template #activator="{ props: activatorProps }">
        <div
          v-bind="activatorProps"
          class="allocation-bar__non-capacity"
          :class="{ 'allocation-bar__non-capacity--collapsed': summary.nonCapacityPos.length > 1 }"
          :aria-label="nonCapacityAriaLabel"
          tabindex="0"
        >
          <span class="allocation-bar__label">{{ nonCapacityLabel }}</span>
        </div>
      </template>
      <div class="allocation-bar__tooltip">
        <p class="allocation-bar__tooltip-line allocation-bar__tooltip-group-heading">Does not consume capacity</p>
        <p v-for="po in summary.nonCapacityPos" :key="(po.id || po.poNumber) as string" class="allocation-bar__tooltip-line">
          {{ po.poNumber }}{{ po.productCategory ? ` · ${categoryTagFor(po.productCategory).label}` : '' }}
        </p>
      </div>
    </v-tooltip>
    <div v-if="summary.status === 'overbooked' && !compact" class="allocation-bar__warning">Overbooked by {{ formatRemaining(summary.overbookedBy, '') }}</div>
    <div class="allocation-bar__utilization" :class="{ 'allocation-bar__utilization--overbooked': compact && summary.status === 'overbooked' }">{{ utilizationLabel }}</div>
  </div>
</template>

<script setup lang="ts">
  import { computed, onBeforeUnmount, ref } from 'vue';
  import AllocationGroupHandle from '@/components/capacity/AllocationGroupHandle.vue';
  import { useAssignmentHover } from '@/composables/useAssignmentHover';
  import { useCalendarDragDrop } from '@/composables/useCalendarDragDrop';
  import { useRecentAssignmentHighlight } from '@/composables/useRecentAssignmentHighlight';
  import { useCalendarPrivacyStore } from '@/store/calendarPrivacyStore';
  import { useCapacityCalendarStore } from '@/store/capacityCalendarStore';
  import type { DistributionGroupMember } from '@/store/capacityCalendarStore';
  import { useCustomerMappingStore } from '@/store/customerMappingStore';
  import { SEGMENT_TYPE } from '@/types/capacityCalendar';
  import type { AllocationSegment, PoAllocation } from '@/types/capacityCalendar';
  import { buildAllocationSegments, formatCapacityQuantity, segmentBlockKey, segmentMatchesBlockKey } from '@/utils/capacityCalendar';
  import { categoryTagFor } from '@/utils/categoryTags';
  import { customerLabel, labelColorFor, resolveCustomerMapping, resolvePoSegmentColor } from '@/utils/customerColors';

  const MIN_LABEL_PERCENT = 12;
  const PERCENT_DECIMALS = 1;
  const MIN_GROUP_MEMBERS = 2;

  const props = defineProps<{
    purchaseOrders: PoAllocation[];
    remainedCapacity: number;
    selected?: boolean;
    // The week this bar's cell represents — needed to match it against
    // calendarStore.recentAssignments for the flash/dim highlight, and as
    // the drag payload's source week. Only set at week granularity.
    weekKey?: string;
    typeCode?: string;
    // Drag-source identity — only set at week granularity (see CapacityCalendar.vue);
    // undefined for any row/period where a portal vendor/capacity type isn't linked.
    rowId?: string;
    vendorId?: string;
    capacityTypeId?: string;
    // The row's capacity unit (e.g. "parts") — null for termination rows, which stay
    // bare numbers. Threaded down from the row so molding cells read "12,400 parts left".
    unit?: string | null;
    // The poNumber, manual-assignment id, or distribution-group id currently selected
    // (drawer open / deep link / scheduling-board block) — matched against segment.key and
    // segment.distributionGroupId; all three live in disjoint spaces (poNumber vs. uuid ids)
    // and never collide. Rings every matching segment across every row, not just the cell
    // the selection came from — that's what makes a distributed PO's or a grouped manual
    // block's other type rows light up too.
    highlightedSegmentKey?: string | null;
    // The week the selection was actually clicked in, where it came from a block at all. A PO
    // segment is keyed by poNumber, so it cannot say which week's assignment it is — but the
    // bar it sits in knows its own week. Absent (the Capacity Calendar) rings every matching
    // segment, as before; supplied, only the clicked week's segments get the ring and the
    // other weeks stay coloured without it.
    highlightedAnchorWeekKey?: string | null;
    // Combined scheduling view: when a PO is selected page-wide, every OTHER
    // assignment segment fades so the selected PO's capacity reads at a glance.
    dimUnhighlighted?: boolean;
    // Slim read-only strip for the scheduling board: no in-bar labels, the row's
    // figures move to the caption. The Capacity Calendar — where segments are dragged —
    // keeps the full-height bar and its larger hit targets.
    compact?: boolean;
    // Segment order, shared by every row in the same week so one block occupies the same
    // rank in each. Omitted anywhere the caller has no intended order.
    orderKeys?: string[];
  }>();

  const emit = defineEmits<{
    selectAllocation: [];
    selectPo: [poNumber: string];
    // blockKey is the segment's distributionGroupId when it belongs to a grouped manual
    // block, else the assignment id — the scheduling board selects whole blocks by it.
    selectManual: [assignmentId: string, blockKey: string];
  }>();

  function formatQuantity(value: number): string {
    return value.toLocaleString();
  }

  // Only for the row's own capacity figures (the "X left" segment, the aria-label's
  // "remaining" phrase, and the overbooked-by warning/aria-label) — a distribution
  // group's other members (see groupBreakdowns below) can belong to a different
  // capacity type with a different unit, so those stay on bare formatQuantity rather
  // than assuming props.unit applies.
  function formatRemaining(value: number, suffix: string): string {
    return formatCapacityQuantity(value, props.unit ?? null, suffix);
  }

  function formatPercent(value: number): string {
    return `${value.toFixed(PERCENT_DECIMALS)}%`;
  }

  const summary = computed(() => buildAllocationSegments(props.purchaseOrders, props.remainedCapacity, props.orderKeys));

  // The compact strip hides every in-bar label, so the two figures the full bar spreads
  // across the remains segment and the overbooked overlay ride along here instead.
  const utilizationLabel = computed(() => {
    if (!props.compact) return `${formatPercent(summary.value.utilizationPercent)} allocated`;
    const leading =
      summary.value.status === 'overbooked'
        ? formatRemaining(summary.value.overbookedBy, 'over')
        : formatRemaining(Math.max(props.remainedCapacity, 0), 'left');
    // Whole percent only — the caption shares one 34px row with the strip, and the exact
    // figure stays in the tooltip and the aria-label.
    return `${leading} · ${Math.round(summary.value.utilizationPercent)}%`;
  });

  const nonCapacityLabel = computed(() => {
    const pos = summary.value.nonCapacityPos;
    if (pos.length === 0) return '';
    const first = (pos[0].poNumber ?? '').replace(/^PO-/, '');
    return pos.length === 1 ? first : `${first} +${pos.length - 1}`;
  });

  const nonCapacityAriaLabel = computed(() => {
    const count = summary.value.nonCapacityPos.length;
    return `${count} non-capacity purchase ${count === 1 ? 'order' : 'orders'}, not counted toward utilization`;
  });

  const calendarStore = useCapacityCalendarStore();
  const customerMappingStore = useCustomerMappingStore();
  const privacyStore = useCalendarPrivacyStore();

  interface GroupBreakdown {
    members: DistributionGroupMember[];
    total: number;
  }

  // Keyed by segment.key (the PO number). Single-member groups are omitted —
  // "distributed across 1 type" is noise.
  const groupBreakdowns = computed(() => {
    const map = new Map<string, GroupBreakdown>();
    summary.value.segments.forEach((segment) => {
      if (segment.type !== SEGMENT_TYPE.po || !segment.distributionGroupId) return;
      const members = calendarStore.distributionGroups.get(segment.distributionGroupId) ?? [];
      if (members.length < MIN_GROUP_MEMBERS) return;
      map.set(segment.key, { members, total: members.reduce((sum, member) => sum + member.quantity, 0) });
    });
    return map;
  });

  // A 'po' segment drags as an individual assignment unless it belongs to a
  // qualifying (>=2 member) distribution group — those drag via the
  // AllocationGroupHandle rendered inside them instead, so every member moves
  // together. A segment with a distributionGroupId whose group happens to have
  // fewer than 2 members (no handle rendered — see groupBreakdowns above) still
  // falls through to plain individual dragging so it's never stuck undraggable.
  const isDragEnabled = computed(
    () => Boolean(props.weekKey && props.rowId && props.vendorId && props.capacityTypeId),
  );

  // groupBreakdowns indexes po segments only, so a manual segment — grouped or not —
  // always falls through as a plain draggable segment.
  function isPlainPoSegment(segment: AllocationSegment): boolean {
    return (segment.type === SEGMENT_TYPE.po || segment.type === SEGMENT_TYPE.manual) && !groupBreakdowns.value.has(segment.key);
  }

  function groupHandleInfo(segment: AllocationSegment): { memberCapacityTypeIds: string[] } | undefined {
    const breakdown = segment.type === SEGMENT_TYPE.po ? groupBreakdowns.value.get(segment.key) : undefined;
    return breakdown ? { memberCapacityTypeIds: breakdown.members.map((member) => member.capacityTypeId) } : undefined;
  }

  const { startDrag, cancelDrag } = useCalendarDragDrop();

  function onSegmentDragStart(event: DragEvent, segment: AllocationSegment): void {
    if (!isPlainPoSegment(segment) || !props.weekKey || !props.rowId || !props.vendorId || !props.capacityTypeId) {
      return;
    }

    const po = props.purchaseOrders.find((candidate) =>
      segment.type === SEGMENT_TYPE.manual ? candidate.id === segment.key : candidate.poNumber === segment.key,
    );
    if (!po?.id) {
      return;
    }

    // Firefox requires setData for the drag to initiate; the move itself is
    // resolved from the module-scoped payload, not this data.
    event.dataTransfer?.setData('text/plain', po.id);
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
    }

    startDrag({
      type: 'assignment',
      rowId: props.rowId,
      weekKey: props.weekKey,
      assignmentId: po.id,
      // Vestigial for a manual block — nothing downstream reads this field for the actual
      // move (completeDrop/moveAssignment are assignmentId-based), but it shouldn't hold a
      // fabricated PO number.
      poNumber: segment.type === SEGMENT_TYPE.manual ? '' : segment.key,
      vendorId: props.vendorId,
      capacityTypeId: props.capacityTypeId,
    });
  }

  // A 'po' segment's key is its poNumber; a 'manual' segment's key is its assignment id —
  // each selects that exact entry in the drawer. The remains segment falls through to the
  // root's @click and opens the plain allocation.
  function onSegmentClick(event: MouseEvent, segment: AllocationSegment): void {
    if (segment.type === SEGMENT_TYPE.po) {
      event.stopPropagation();
      emit('selectPo', segment.key);
      return;
    }
    if (segment.type === SEGMENT_TYPE.manual) {
      event.stopPropagation();
      emit('selectManual', segment.key, segment.distributionGroupId ?? segment.key);
    }
  }

  function onSegmentDragEnd(): void {
    // No-op if a compatible drop already resolved and cleared this — only
    // matters when the drag was cancelled or dropped somewhere invalid.
    cancelDrag();
  }

  const { isActiveForWeek, matchingPoNumbers } = useRecentAssignmentHighlight();
  const weekIsHighlighted = computed(() => isActiveForWeek(props.weekKey));
  const highlightedPoNumbers = computed(() => matchingPoNumbers(props.weekKey));

  function segmentHighlightClass(segment: AllocationSegment): string | undefined {
    if (segment.type !== SEGMENT_TYPE.po || !weekIsHighlighted.value) {
      return undefined;
    }
    // segment.key is the po.poNumber for 'po' segments (see buildAllocationSegments).
    return highlightedPoNumbers.value.has(segment.key) ? 'capacity-highlight-flash' : 'capacity-highlight-dim';
  }

  const { hoveredBlockKey, setHoveredBlock, clearHoveredBlock } = useAssignmentHover();
  // Tracks whether the live hover state was set by this bar, so unmounting mid-hover
  // clears it without stomping a hover another bar has since taken over.
  const thisBarSetHover = ref(false);

  // Every block hovers, whatever its type and whether or not it is distributed — hover and
  // selection ring the same things, so pointing at a manual block lights up its sibling rows
  // exactly as pointing at a PO does.
  function onSegmentHoverStart(segment: AllocationSegment): void {
    const blockKey = segmentBlockKey(segment);
    if (!blockKey) {
      return;
    }
    setHoveredBlock(blockKey);
    thisBarSetHover.value = true;
  }

  function onSegmentHoverEnd(): void {
    if (!thisBarSetHover.value) {
      return;
    }
    clearHoveredBlock();
    thisBarSetHover.value = false;
  }

  onBeforeUnmount(onSegmentHoverEnd);

  function segmentHoverRingClass(segment: AllocationSegment): string | undefined {
    return segmentMatchesBlockKey(segment, hoveredBlockKey.value) ? 'capacity-group-ring' : undefined;
  }

  function matchesHighlight(segment: AllocationSegment): boolean {
    return segmentMatchesBlockKey(segment, props.highlightedSegmentKey);
  }

  // Undimmed means "this block"; the ring narrows that to "the one you clicked".
  const isAnchorBar = computed(() => {
    const anchorWeekKey = props.highlightedAnchorWeekKey;
    return anchorWeekKey === undefined || anchorWeekKey === null || anchorWeekKey === props.weekKey;
  });

  function segmentSelectionRingClass(segment: AllocationSegment): string | undefined {
    return matchesHighlight(segment) && isAnchorBar.value ? 'allocation-bar__segment--highlighted' : undefined;
  }

  // The block under the pointer is never dimmed: greyscale applies to the element's
  // box-shadow too, so a dimmed segment would draw its hover ring in grey — and pointing at
  // something is a request to see it, not a reason to keep it faded.
  // The same block in another week: softened rather than greyed, so it still reads as the same
  // customer's colour and its quantity stays comparable, while the clicked week keeps the ring.
  // Hue is deliberately left alone — greyscale here would make the family unrecognisable.
  function segmentRelatedClass(segment: AllocationSegment): string | undefined {
    // Only where the dimming ramp exists: with nothing else faded, softening the related
    // weeks would make the one tier that matters the only faded thing on screen.
    if (!props.dimUnhighlighted || isAnchorBar.value || !matchesHighlight(segment)) return undefined;
    return 'allocation-bar__segment--related';
  }

  function segmentMutedClass(segment: AllocationSegment): string | undefined {
    if (!props.dimUnhighlighted || !props.highlightedSegmentKey) return undefined;
    if (segment.type === SEGMENT_TYPE.remains || matchesHighlight(segment)) return undefined;
    if (segmentMatchesBlockKey(segment, hoveredBlockKey.value)) return undefined;
    return 'allocation-bar__segment--muted';
  }

  const barAriaLabel = computed(() => {
    if (summary.value.status === 'overbooked') {
      return `Capacity overbooked by ${formatRemaining(summary.value.overbookedBy, '')}`;
    }
    return `${formatPercent(summary.value.utilizationPercent)} allocated, ${formatRemaining(props.remainedCapacity, 'remaining')}`;
  });

  function segmentColor(segment: AllocationSegment, index: number): string | undefined {
    if (segment.type !== SEGMENT_TYPE.po) return undefined;
    return resolvePoSegmentColor(segment.key, customerMappingStore.items, {
      hideCustomerMappings: privacyStore.hideCustomerMappings,
      index,
    });
  }

  function segmentLabelStyle(segment: AllocationSegment, index: number): { color: string } | undefined {
    const fill = segmentColor(segment, index);
    return fill ? { color: labelColorFor(fill) } : undefined;
  }

  function segmentCustomerName(segment: AllocationSegment): string | null {
    if (segment.type !== SEGMENT_TYPE.po) return null;
    const mapping = resolveCustomerMapping(segment.key, customerMappingStore.items);
    return mapping ? customerLabel(mapping) : null;
  }

  function segmentLabel(segment: AllocationSegment): string {
    if (segment.type === SEGMENT_TYPE.po) return `${segment.label.replace('PO-', '')} · ${formatQuantity(segment.quantity)}`;
    if (segment.type === SEGMENT_TYPE.manual) return `${segment.label} · ${formatQuantity(segment.quantity)}`;
    return formatRemaining(segment.quantity, 'left');
  }

  function segmentAriaLabel(segment: AllocationSegment): string {
    return `${segment.label}, quantity ${formatQuantity(segment.quantity)}, ${formatPercent(segment.percent)} of represented capacity`;
  }
</script>

<style scoped>
  .allocation-bar {
    position: relative;
    display: flex;
    width: 100%;
    min-height: 4rem;
    overflow: hidden;
    border: 1px solid rgb(203 213 225);
    border-radius: 0.375rem;
    background: rgb(248 250 252);
    cursor: pointer;
    transition:
      border-color 150ms ease,
      box-shadow 150ms ease,
      transform 150ms ease;
  }

  .allocation-bar:hover {
    border-color: rgb(59 130 246);
    box-shadow: 0 0 0 2px rgb(191 219 254);
    transform: translateY(-1px);
  }

  .allocation-bar:focus-visible {
    outline: 2px solid rgb(37 99 235);
    outline-offset: 2px;
  }

  /* Persistent state while the allocation is open in the drawer — same visual language as hover. */
  .allocation-bar--selected,
  .allocation-bar--selected:hover {
    border-color: rgb(37 99 235);
    box-shadow: 0 0 0 2px rgb(147 197 253);
    transform: translateY(-1px);
  }

  .allocation-bar--overbooked.allocation-bar--selected {
    border-color: rgb(220 38 38);
    box-shadow: 0 0 0 2px rgb(254 202 202);
  }

  .allocation-bar__segment {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 0;
    height: 100%;
    min-height: 4rem;
    padding: 0 0.375rem;
    border-top: 0;
    border-bottom: 0;
    border-left: 0;
    border-right: 1px solid rgb(255 255 255 / 60%);
    font: inherit;
    cursor: inherit;
  }

  .allocation-bar__segment:focus-visible {
    outline: 2px solid rgb(37 99 235);
    outline-offset: -2px;
  }

  .allocation-bar__segment:last-child {
    border-right: 0;
  }

  .allocation-bar__segment--remains {
    background: rgb(241 245 249);
    color: rgb(71 85 105);
  }

  .allocation-bar__segment--manual {
    background: repeating-linear-gradient(135deg, rgb(254 243 199), rgb(254 243 199) 4px, rgb(253 230 138) 4px, rgb(253 230 138) 8px);
    color: rgb(120 53 15);
  }

  /* Group hover ring — inset because the bar clips overflow; z-index lifts it above
     neighboring segment borders but stays below .allocation-bar__warning (2) so it never covers the overbooked overlay. */
  .capacity-group-ring {
    position: relative;
    z-index: 1;
    border-radius: 0.25rem;
    box-shadow:
      inset 0 0 0 2px rgb(37 99 235),
      inset 0 0 0 4px rgb(191 219 254);
  }

  /* Selected-PO/manual-block ring (drawer selection or a Scheduling-board deep link) — same
     inset-box-shadow technique as .capacity-group-ring above and for the same reason: the bar's
     overflow: hidden would clip a plain outline. Kept as its own rule (rather than folded into
     capacity-group-ring) because the two are independent states — a segment can be the drawer's
     current selection while a *different* segment's distribution group is hover-highlighted —
     and z-index: 2 keeps this ring on top if both ever land on the same segment. */
  .allocation-bar__segment--highlighted {
    position: relative;
    z-index: 2;
    border-radius: 0.25rem;
    box-shadow:
      inset 0 0 0 2px rgb(37 99 235),
      inset 0 0 0 4px rgb(255 255 255);
  }

  /* The selection ramp, strongest to weakest: the clicked block keeps its ring and full
     colour, the same block in other weeks softens, everything else greys out. */
  .allocation-bar__segment--related {
    opacity: 0.7;
  }

  .allocation-bar__segment--muted {
    filter: grayscale(1);
    opacity: 0.35;
  }

  .allocation-bar__label {
    overflow: hidden;
    font-size: 0.6875rem;
    font-weight: 600;
    line-height: 1.2;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .allocation-bar__tooltip {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    max-width: 16rem;
  }

  .allocation-bar__tooltip-line {
    margin: 0;
    font-size: 0.75rem;
    line-height: 1.35;
  }

  .allocation-bar__tooltip-group {
    margin-top: 0.25rem;
    padding-top: 0.25rem;
    border-top: 1px solid rgb(255 255 255 / 35%);
  }

  .allocation-bar__tooltip-group-heading,
  .allocation-bar__tooltip-member--current {
    font-weight: 600;
  }

  /* Non-capacity POs occupy a constant slice, never proportional width — they cost
     0 capacity, so they must never read as a share of the bar. */
  .allocation-bar__non-capacity {
    display: flex;
    flex: 0 0 3rem;
    align-items: center;
    justify-content: center;
    /* min-width: 0 lets the fixed flex-basis win over the nowrap label's content
       width — without it the slice grows to fit the full PO number. */
    min-width: 0;
    overflow: hidden;
    min-height: 4rem;
    padding: 0 0.25rem;
    border-left: 1px dashed rgb(148 163 184);
    background: repeating-linear-gradient(135deg, rgb(241 245 249), rgb(241 245 249) 4px, rgb(226 232 240) 4px, rgb(226 232 240) 8px);
    color: rgb(71 85 105);
  }

  .allocation-bar__non-capacity--collapsed {
    flex-basis: 4.5rem;
  }

  .allocation-bar__utilization {
    position: absolute;
    right: 0.25rem;
    bottom: 0.125rem;
    padding: 0.0625rem 0.25rem;
    border-radius: 0.25rem;
    background: rgb(255 255 255 / 85%);
    color: rgb(71 85 105);
    font-size: 0.5625rem;
    font-weight: 600;
    pointer-events: none;
  }

  .allocation-bar__warning {
    position: absolute;
    left: 0.25rem;
    top: 0.125rem;
    z-index: 2;
    padding: 0.0625rem 0.25rem;
    border-radius: 0.25rem;
    background: rgb(254 226 226 / 92%);
    color: rgb(153 27 27);
    font-size: 0.5625rem;
    font-weight: 700;
    pointer-events: none;
  }

  /* Double weight, not just red: at the compact strip's height a 1px line read as a hairline
     next to the healthy rows. Border rather than an added box-shadow, because the hover and
     selected states already own box-shadow as their outer ring. Everything is border-box, so
     the bar keeps its outer size and the extra pixel comes off the segment area. */
  .allocation-bar--overbooked {
    border-width: 2px;
    border-color: rgb(239 68 68);
  }

  /* Compact strip (scheduling board). The reserved padding-right is what the caption sits
     in: segment percentages resolve against the content box, so they stay exact instead of
     being squeezed by a sibling. */
  .allocation-bar--compact {
    min-height: 1.25rem;
    padding-right: 6.75rem;
    border-radius: 0.25rem;
  }

  .allocation-bar--compact:hover,
  .allocation-bar--compact.allocation-bar--selected {
    transform: none;
  }

  .allocation-bar--compact .allocation-bar__segment,
  .allocation-bar--compact .allocation-bar__non-capacity {
    min-height: 1.25rem;
    padding: 0;
  }

  .allocation-bar--compact .allocation-bar__non-capacity {
    flex-basis: 1.5rem;
  }

  .allocation-bar--compact .allocation-bar__label {
    display: none;
  }

  .allocation-bar--compact .allocation-bar__utilization {
    top: 50%;
    right: 0.375rem;
    bottom: auto;
    overflow: hidden;
    max-width: 6.25rem;
    padding: 0;
    background: transparent;
    font-size: 0.625rem;
    font-weight: 500;
    text-overflow: ellipsis;
    white-space: nowrap;
    transform: translateY(-50%);
  }

  /* Compounded with --compact deliberately: `.allocation-bar--compact .allocation-bar__utilization`
     above sets font-weight 500, and the bare modifier (one class) lost to it on specificity
     despite coming later — so the overbooked caption has been rendering at normal weight. The
     modifier is only ever emitted in compact mode, so nothing else needs it. */
  .allocation-bar--compact .allocation-bar__utilization--overbooked {
    color: rgb(153 27 27);
    font-weight: 700;
  }
</style>
